import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { GripVertical, X, Plus } from "lucide-react";

interface TemplateQuestionsManagerProps {
  templateId: string;
}

interface LinkedQuestion {
  question_id: string;
  order_index: number;
  questions: {
    id: string;
    question_text: string;
  };
}

export const TemplateQuestionsManager = ({ templateId }: TemplateQuestionsManagerProps) => {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");

  // Fetch linked questions for this template
  const { data: linked, isLoading } = useQuery({
    queryKey: ["template-questions", templateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("survey_template_questions")
        .select(
          `question_id, order_index, questions(id, question_text)`
        )
        .eq("template_id", templateId)
        .order("order_index");
      if (error) throw error;
      return (data || []) as LinkedQuestion[];
    },
  });

  const linkedIds = useMemo(() => new Set((linked || []).map((q) => q.question_id)), [linked]);

  // Search questions not yet linked
  const { data: available } = useQuery({
    queryKey: ["available-questions", templateId, search],
    queryFn: async () => {
      let query = supabase
        .from("questions")
        .select("id, question_text")
        .order("order_index");

      if (search) {
        query = query.ilike("question_text", `%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []).filter((q: any) => !linkedIds.has(q.id));
    },
  });

  const handleRemove = async (questionId: string) => {
    const { error } = await supabase
      .from("survey_template_questions")
      .delete()
      .eq("template_id", templateId)
      .eq("question_id", questionId);
    if (error) {
      toast.error("Failed to remove question");
    } else {
      toast.success("Question removed");
      qc.invalidateQueries({ queryKey: ["template-questions", templateId] });
      qc.invalidateQueries({ queryKey: ["admin-survey-templates"] });
    }
  };

  const handleAdd = async (questionId: string) => {
    // Find next order
    const last = (linked || []).reduce((max, q) => Math.max(max, q.order_index || 0), 0);
    const nextOrder = last + 1;
    const { error } = await supabase
      .from("survey_template_questions")
      .insert({ template_id: templateId, question_id: questionId, order_index: nextOrder });
    if (error) {
      toast.error("Failed to add question");
    } else {
      toast.success("Question added");
      qc.invalidateQueries({ queryKey: ["template-questions", templateId] });
      qc.invalidateQueries({ queryKey: ["admin-survey-templates"] });
      setSearch("");
    }
  };

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination || !linked) return;
    const items = Array.from(linked);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    // Recompute order_index sequentially starting at 1
    const updates = items.map((item, idx) => ({ ...item, order_index: idx + 1 }));

    try {
      // Apply updates in parallel
      await Promise.all(
        updates.map((u) =>
          supabase
            .from("survey_template_questions")
            .update({ order_index: u.order_index })
            .eq("template_id", templateId)
            .eq("question_id", u.question_id)
        )
      );
      qc.invalidateQueries({ queryKey: ["template-questions", templateId] });
      toast.success("Order updated");
    } catch (e) {
      toast.error("Failed to reorder");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input
          placeholder="Search questions to add..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="text-sm text-muted-foreground">{available?.length || 0} results</span>
      </div>
      <div className="max-h-40 overflow-y-auto border rounded-md p-2">
        <div className="grid grid-cols-1 gap-2">
          {(available || []).slice(0, 20).map((q: any) => (
            <div key={q.id} className="flex items-center justify-between">
              <div className="text-sm truncate pr-2">{q.question_text}</div>
              <Button size="sm" variant="secondary" onClick={() => handleAdd(q.id)}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          ))}
          {search && (available || []).length === 0 && (
            <div className="text-sm text-muted-foreground">No questions found.</div>
          )}
        </div>
      </div>

      <Separator />

      <div>
        <div className="text-sm font-medium mb-2">Linked Questions</div>
        <div className="border rounded-md">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading...</div>
          ) : (linked || []).length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">No questions linked yet.</div>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="tq-list">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {(linked || []).map((item, index) => (
                      <Draggable key={item.question_id} draggableId={item.question_id} index={index}>
                        {(drag) => (
                          <div
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            className="flex items-center justify-between p-2 border-b last:border-b-0 bg-background"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span {...drag.dragHandleProps} className="text-muted-foreground">
                                <GripVertical className="h-4 w-4" />
                              </span>
                              <Badge variant="outline" className="shrink-0">{index + 1}</Badge>
                              <div className="truncate">{item.questions.question_text}</div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemove(item.question_id)}
                              aria-label="Remove question"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </div>
    </div>
  );
};
